import React from 'react';
import { observer } from 'mobx-react';
import { isObservableArray } from 'mobx';
import { Link } from 'react-router-dom';

// Helpers
import { getParam, getURLSegment, toggleDrawer } from '../../helpers/helpers.js'

// Translations
import translate from '../Translate.js';

// Dependencies
import moment from 'moment';
import DOMPurify from 'dompurify';

// Components
import LoadMore from '../Common/LoadMore';

const Report = observer(class Report extends React.Component {
  constructor() {
    super();

    // Bind this to functions
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(e, action, actionType, sids) {
    if(actionType === 'drawer') {
      e.preventDefault();

      const _this = this;
      const url = e.target.href;
      const sessionSid = getParam('session', url);

      this.props.parent.setState({
        sessionSid
      }, function(){
        toggleDrawer(null, _this.props.parent, action);
      });
    } else {
      this.reportActions[action](sids, e);
    }
  }

  render() {
    const _this = this;
    const { translation } = this.props;
    const { ReportStore, SessionStore } = this.props.store;
    const { reportData, appendReportData, reportFormat, reportNext } = ReportStore;

    let group = null;
    let concat = false;

    if(getURLSegment(this, 2) === 'sessions') {
      group = this.props.store.ScheduleStore.events;
      concat = true;
    }

    function renderData(element, index) {
      if(element && typeof element === 'object' && !Array.isArray(element)) {
        // If it’s an object, check for a URL and title
        if(element.url && element.title) {
          // If it’s a link, output an anchor tag
          if(element.target && element.target === '_self') {
            return (
              <li key={index}>
                <Link
                  to={element.url}
                  className={element.class}
                  onClick={(e) => {_this.handleClick(e, element.action, element.actionType)}}
                >
                  {element.title}
                </Link>
              </li>
            )
          } else {
            return (
              <li key={index}>
                <a
                  href={element.url}
                  target="_blank"
                  className={element.class}
                  onClick={(e) => {_this.handleClick(e, element.action, element.actionType)}}
                >
                  {element.title}
                </a>
              </li>
            )
          }
        } else if(element.button) {
          return (
            <li key={index}>
              <button
                className={element.class}
                type="button"
                onClick={(e) => {_this.handleClick(e, element.action, null, element.sids)}}
                title={element.helper}
              >
                {element.title}
              </button>
            </li>
          )
        } else if(element.raw && element.formatted) {
          // Otherwise, if it’s raw and formatted data, output the formatted data
          return element.formatted;
        } else {
          // If it’s not a link, loop through the keys
          return (
            Object.keys(element).map(
              (key, i) => {
              if(element[key]) {
                return (
                  <li key={i}>
                    {/* Time data */}
                    {
                      moment(element[key], moment.ISO_8601).isValid() &&

                      <time>
                        {moment(element[key]).local()
                                             .tz(moment.tz.guess())
                                             .format('M/D/YYYY [at] h:mma z')}
                      </time>
                    }

                    {/* Internal link */}
                    {
                      (
                        !moment(element[key], moment.ISO_8601).isValid() &&
                        typeof element[key] === 'object' &&
                        element[key].url &&
                        !element[key].mailto
                      ) &&

                      <Link
                        to={element[key].url}
                        target="_blank"
                      >
                        {element[key].title}
                      </Link>
                    }

                    {/* External link */}
                    {
                      (
                        !moment(element[key], moment.ISO_8601).isValid() &&
                        typeof element[key] === 'object' &&
                        element[key].mailto
                      ) &&

                      <a href={element[key].url}>
                        {element[key].title}
                      </a>
                    }

                    {/* Button */}
                    {
                      (
                        !moment(element[key], moment.ISO_8601).isValid() &&
                        typeof element[key] === 'object' &&
                        element[key].onClick
                      ) &&

                      <button
                        className="btn"
                        onClick={_this[element[key].onClick]}
                        data-sid={element[key].sid}
                      >
                        {element[key].title}
                      </button>
                    }

                    {/* Object */}
                    {
                      (
                        !moment(element[key], moment.ISO_8601).isValid() &&
                        typeof element[key] === 'object' &&
                        !element[key].url && !element[key].onClick
                      ) &&

                      <span>object</span>
                    }

                    {/* Truncated string */}
                    {
                      (
                        !moment(element[key], moment.ISO_8601).isValid() &&
                        typeof element[key] !== 'object' &&
                        key === 'truncate'
                      ) &&

                      <span
                        className="truncate"
                        title={element[key]}
                      >
                        {element[key].substring(0, 4)}
                        …
                        {element[key].slice(-4)}
                      </span>
                    }

                    {/* String */}
                    {
                      (
                        !moment(element[key], moment.ISO_8601).isValid() &&
                        typeof element[key] !== 'object' &&
                        key !== 'truncate'
                      ) &&

                      element[key]
                    }
                  </li>
                )
              }

              return null;
            })
          )
        }
      } else {
        return (
          <li key={index}>{element}</li>
        )
      }
    }

    if(!SessionStore.loading) {
      if(reportData.length > 0) {
        return (
          <div className="table-scrollable">
            <div className="table-wrap">
              <table className="report-table">
                {/* Fixed table head */}
                <thead>
                  <tr>
                    {Object.keys(reportData[0]).map(
                      (key, i) =>
                      <th
                        id={`heading-${i}`}
                        key={i}
                        scope="col"
                        className={`subtitle column-${key}`}
                      >
                        {translation[key] || key}

                        <div
                          className={`th-fixed column-${key}`}
                          data-direction="asc"
                          aria-hidden="true"
                        >
                          {translation[key] || key}
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>

                {/* Table body */}
                <tbody>
                  {/* Loop through each array object then loop through each object key to output the data in the correct column */}
                  {reportData.map(
                    (key, i) =>
                    <tr key={i}>
                      {Object.keys(reportData[0]).map(
                        (column, j) =>
                        {
                          if(reportData[i][column] && typeof reportData[i][column] === 'object' && !isObservableArray(reportData[i][column])) {
                            // If the data is an object, loop through it
                            return (
                              <td
                                aria-labelledby={`heading-${j}`}
                                key={j}
                                data-col={column}
                                className={`column-${column}`}
                                data-value={reportData[i][column].raw || ''}
                              >
                                <ul className="table-list">
                                  {renderData(reportData[i][column])}
                                </ul>
                              </td>
                            )
                          } else if(isObservableArray(reportData[i][column])) {
                            // Otherwise, if it’s an array, loop through that
                            return (
                              <td
                                aria-labelledby={`heading-${j}`}
                                key={j}
                                data-col={column}
                                className={`column-${column}`}
                              >
                                <ul className="table-list">
                                  {reportData[i][column].map(
                                    (subKey, k) =>
                                    renderData(subKey, k)
                                  )}
                                </ul>
                              </td>
                            )
                          } else {
                            // Otherwise, just output the data
                            return (
                              <td
                                aria-labelledby={`heading-${j}`}
                                key={j}
                                className={`${typeof reportData[i][column] === 'number' ? 'number' : ''} column-${column}`}
                                data-col={column}
                                data-value={reportData[i][column]}
                              >
                                {/* If it’s a date, format it */}
                                {moment(reportData[i][column], moment.ISO_8601).isValid() && column !== 'password' &&
                                  <time>{moment(reportData[i][column]).local().tz(moment.tz.guess()).format('M/D/YYYY [at] h:mma z')}</time>
                                }

                                {/* Otherwise, just output it */}
                                {(!moment(reportData[i][column], moment.ISO_8601).isValid() || column === 'password') &&
                                  <div dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(reportData[i][column])
                                  }} />
                                }
                              </td>
                            )
                          }
                        }
                      )}
                    </tr>
                  )}
                </tbody>
              </table>

              {reportNext &&
                <LoadMore
                  url={reportNext}
                  text="Load more results"
                  dataFormat={reportFormat}
                  dataSet={appendReportData}
                  concat={concat}
                  group={group}
                  wrapper={'.table-wrap'}
                  {...this.props}
                />
              }
            </div>
          </div>
        );
      } else {
        return (
          <span className="no-results">{translation.no_results}</span>
        )
      }
    }

    return null;
  }
})

export default translate('Report')(Report);



// WEBPACK FOOTER //
// ./src/app/components/Reports/Report.js